import { environment } from '@environments/environment';
import {
  ActivityProduct,
  ActivityProductDistribution,
  ActivityProductImage,
  ActivityProductOption,
  ActivityProductRestrictions,
  QuantityType,
} from '@app/_store';
import { Store } from '@ngxs/store';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ActivityProductService {
  constructor(
    private http: HttpClient,
    private store: Store
  ) {}

  createProduct(
    organizationId: string,
    activityId: string,
    product: {
      title: string;
      description: string;
      amount: number;
      profit?: number;
      isUserSelectable: boolean;
      quantityType: QuantityType;
      defaultQuantity?: number;
      isDeliverable: boolean;
      requireComment: boolean;
      options: ActivityProductOption[];
      restrictions: ActivityProductRestrictions;
      costDistributions: ActivityProductDistribution[];
      profitDistributions: ActivityProductDistribution[];
    }
  ): Observable<ActivityProduct> {
    return this.http.post<ActivityProduct>(
      `${environment.apiUrl}/organizations/${organizationId}/activities/${activityId}/products`,
      product
    );
  }

  deleteProduct(organizationId: string, activityId: string, productId: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiUrl}/organizations/${organizationId}/activities/${activityId}/products/${productId}`
    );
  }

  unlockProduct(organizationId: string, activityId: string, productId: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiUrl}/organizations/${organizationId}/activities/${activityId}/products/${productId}/lock`
    );
  }

  changeProductOrder(
    organizationId: string,
    activityId: string,
    productId: string,
    direction: 'up' | 'down'
  ): Observable<{ productOrder: string[] }> {
    return this.http.post<{ productOrder: string[] }>(
      `${environment.apiUrl}/organizations/${organizationId}/activities/${activityId}/products/${productId}/sort?direction=${direction}`,
      null
    );
  }

  moveProduct(
    organizationId: string,
    activityId: string,
    productId: string,
    position: number
  ): Observable<{ productOrder: string[] }> {
    return this.http.post<{ productOrder: string[] }>(
      `${environment.apiUrl}/organizations/${organizationId}/activities/${activityId}/products/${productId}/sort?position=${position}`,
      null
    );
  }

  addProductImage(organizationId: string, activityId: string, productId: string, file: File): Observable<ActivityProductImage> {
    const formData: FormData = new FormData();
    formData.append('file', file);

    return this.http.post<ActivityProductImage>(
      `${environment.apiUrl}/organizations/${organizationId}/activities/${activityId}/products/${productId}/images`,
      formData
    );
  }

  deleteProductImage(organizationId: string, activityId: string, productId: string, imageId: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiUrl}/organizations/${organizationId}/activities/${activityId}/products/${productId}/images/${imageId}`
    );
  }

  updateProduct(
    organizationId: string,
    activityId: string,
    productId: string,
    product: {
      title: string;
      description: string;
      amount: number;
      profit?: number;
      isUserSelectable: boolean;
      quantityType: QuantityType;
      defaultQuantity?: number;
      isDeliverable: boolean;
      requireComment: boolean;
      options: ActivityProductOption[];
      restrictions: ActivityProductRestrictions;
      costDistributions: ActivityProductDistribution[];
      profitDistributions: ActivityProductDistribution[];
    },
    update: string
  ): Observable<ActivityProduct> {
    let extraArguments = '';
    if (update) {
      extraArguments = '?update=' + update;
    }

    return this.http.put<ActivityProduct>(
      `${environment.apiUrl}/organizations/${organizationId}/activities/${activityId}/products/${productId}${extraArguments}`,
      product
    );
  }

  copyFromActivity(
    organizationId: string,
    activityId: string,
    fromActivityId: string,
    isArchived: boolean
  ): Observable<{ productCount: number }> {
    return this.http.post<{ productCount: number }>(
      `${environment.apiUrl}/organizations/${organizationId}/activities/${activityId}/products/copy?fromActivityId=${fromActivityId}&isArchived=${isArchived}`,
      null
    );
  }
}
